import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { ApiError, request, type Method, type RequestOptions } from '../../lib/api';
import * as api from './session';

export type AuthStatus = 'restoring' | 'authenticated' | 'anonymous';

interface AuthState {
  status: AuthStatus;
  session: api.SessionResponse | null;
}

/** Calls the API with the current access token (see AuthProvider). */
export type AuthedRequest = <T>(
  method: Method,
  path: string,
  body?: unknown,
  opts?: Pick<RequestOptions, 'signal' | 'timeoutMs'>,
) => Promise<T>;

interface AuthContextValue extends AuthState {
  login: (creds: api.LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  /** UI gating only: the API authorizes every request itself. */
  can: (permission: string) => boolean;
  request: AuthedRequest;
  /**
   * The current access token, for requests the app cannot route through
   * `request` (media players loading HLS). Kept fresh by proactive renewal.
   */
  accessToken: () => string | undefined;
  /** Reflects organisation settings saved by the user without a round trip. */
  setTenant: (tenant: api.SessionTenant) => void;
  /** Super administrators: work inside another tenant, and come back (ADR-028). */
  enterTenant: (tenantId: string) => Promise<void>;
  leaveTenant: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Refresh this long before the access token expires. */
const REFRESH_MARGIN_S = 60;
const MIN_REFRESH_DELAY_S = 10;
const RETRY_DELAY_S = 30;

/**
 * Holds the access token in memory only; the refresh token lives in an
 * HttpOnly cookie the page cannot read. On load the session is restored from
 * that cookie, and the access token is renewed shortly before it expires.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'restoring', session: null });

  // Token used by in-flight requests. It is updated synchronously when a session
  // is adopted: protected pages mount in the same commit and request immediately,
  // before any effect of this provider would have run.
  const tokenRef = useRef<string | undefined>(undefined);
  const tenantRef = useRef<string | undefined>(undefined);
  const qc = useQueryClient();

  const adopt = useCallback(
    (session: api.SessionResponse | null) => {
      tokenRef.current = session?.access_token;
      // Never show one tenant's cached data in another: entering or leaving a
      // tenant here, or in another tab sharing the refresh cookie, or signing
      // in as someone else, starts from an empty cache.
      const tenant = session?.tenant.id;
      if (tenant !== tenantRef.current) {
        if (tenantRef.current !== undefined) qc.clear();
        tenantRef.current = tenant;
      }
      setState(
        session ? { status: 'authenticated', session } : { status: 'anonymous', session: null },
      );
    },
    [qc],
  );

  // Restore the session from the refresh cookie on load.
  useEffect(() => {
    let active = true;
    api.refresh().then(
      (s) => {
        if (active) adopt(s);
      },
      () => {
        if (active) adopt(null);
      },
    );
    return () => {
      active = false;
    };
  }, [adopt]);

  // Renew the access token shortly before it expires; every new session re-arms the timer.
  const session = state.session;
  useEffect(() => {
    if (!session) return;
    let timer: number | undefined;
    const tick = () => {
      api.refresh().then(adopt, (err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          adopt(null);
          return;
        }
        // Transient failure (network, 503): keep the current token and retry.
        timer = window.setTimeout(tick, RETRY_DELAY_S * 1000);
      });
    };
    const delay = Math.max(session.expires_in - REFRESH_MARGIN_S, MIN_REFRESH_DELAY_S);
    timer = window.setTimeout(tick, delay * 1000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [session, adopt]);

  /**
   * A missing or expired access token (401 unauthenticated / invalid_token)
   * triggers one refresh and a retry. A session that is no longer valid, or a
   * user that was disabled (401 invalid_session), signs the console out.
   */
  const authedRequest = useCallback<AuthedRequest>(
    async <T,>(
      method: Method,
      path: string,
      body?: unknown,
      opts: Pick<RequestOptions, 'signal' | 'timeoutMs'> = {},
    ): Promise<T> => {
      const send = () => {
        const token = tokenRef.current;
        return request<T>(method, path, body, token ? { ...opts, token } : opts);
      };
      try {
        return await send();
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 401) throw err;
        if (err.code !== 'invalid_token' && err.code !== 'unauthenticated') {
          adopt(null);
          throw err;
        }
        try {
          adopt(await api.refresh());
        } catch (refreshErr) {
          if (refreshErr instanceof ApiError && refreshErr.status === 401) adopt(null);
          throw err;
        }
        return send();
      }
    },
    [adopt],
  );

  const accessToken = useCallback(() => tokenRef.current, []);

  const permissions = session?.permissions;
  const can = useCallback((p: string) => permissions?.includes(p) ?? false, [permissions]);

  const setTenant = useCallback((tenant: api.SessionTenant) => {
    setState((s) => (s.session ? { ...s, session: { ...s.session, tenant } } : s));
  }, []);

  const login = useCallback(
    async (creds: api.LoginCredentials) => {
      adopt(await api.login(creds));
    },
    [adopt],
  );

  const token = state.session?.access_token;
  const logout = useCallback(async () => {
    try {
      await api.logout(token);
    } finally {
      // Local sign-out happens even if the server is unreachable.
      adopt(null);
    }
  }, [adopt, token]);

  const enterTenant = useCallback(
    async (tenantId: string) => {
      adopt(await api.enterTenant(tokenRef.current ?? '', tenantId));
    },
    [adopt],
  );
  const leaveTenant = useCallback(async () => {
    adopt(await api.leaveTenant(tokenRef.current ?? ''));
  }, [adopt]);

  const value = useMemo(
    () => ({
      ...state,
      login,
      logout,
      can,
      request: authedRequest,
      setTenant,
      accessToken,
      enterTenant,
      leaveTenant,
    }),
    [state, login, logout, can, authedRequest, setTenant, accessToken, enterTenant, leaveTenant],
  );
  return <AuthContext value={value}>{children}</AuthContext>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
